/**
 * SECTIONS registry — 各 section type のコンポーネント・スキーマ・サンプルを統合
 */
import type { ComponentType } from 'react';
import type { BrandContext, SectionStyle, SectionType, VisualIdentity } from '../types/index';
import * as schemas from '../schemas/index';

// Components
import { heroVariants } from './hero';
import { socialProofVariants } from './social_proof';
import { statsVariants } from './stats';
import { productShowcaseVariants } from './product_showcase';
import { videoVariants } from './video';
import { imageVariants } from './image';
import { featureGridVariants } from './feature_grid';
import { bentoVariants } from './bento';
import { howItWorksVariants } from './how_it_works';
import { testimonialsVariants } from './testimonials';
import { pricingVariants } from './pricing';
import { comparisonVariants } from './comparison';
import { faqVariants } from './faq';
import { ctaBannerVariants } from './cta_banner';
import { newsletterVariants } from './newsletter';
import { teamVariants } from './team';
import { speakerVariants } from './speaker';
import { locationVariants } from './location';
import { countdownVariants } from './countdown';
import { featureSpotlightVariants } from './feature_spotlight';
import { footerVariants } from './footer';

/** バリアントコンポーネントの共通 Props */
export interface SectionVariantProps {
  content: any;
  identity?: VisualIdentity;
  style?: SectionStyle;
  /** ランディング全体のブランド情報（Hero ロゴ / Footer SNS で使用） */
  brand?: BrandContext;
}

interface SectionEntry {
  type: SectionType;
  /** UI 表示用の日本語ラベル */
  label: string;
  /** variant の内部 ID → 日本語表示名 */
  variantLabels: Record<string, string>;
  variants: Record<string, ComponentType<SectionVariantProps>>;
  aiSchema: unknown;
  uiSchema: unknown;
  zodSchema: unknown;
  sample: unknown;
  /**
   * CTA ボタンを保有し、style.ctaAlign が描画に効くか。
   * SectionEditorSheet が CTA 位置フィールドを表示するかの判定に使う。
   */
  hasCta?: boolean;
}

export const SECTIONS: Record<SectionType, SectionEntry> = {
  hero: {
    type: 'hero',
    label: 'ヒーロー',
    variantLabels: {
      centered: '中央寄せ',
      split: '左右分割',
      editorial: 'エディトリアル',
      image_bg: '背景画像',
      event: 'イベント告知用',
    },
    variants: heroVariants,
    aiSchema: schemas.heroAISchema,
    uiSchema: schemas.heroUISchema,
    zodSchema: schemas.heroZodSchema,
    sample: schemas.heroSample,
    hasCta: true,
  },
  social_proof: {
    type: 'social_proof',
    label: '信頼の証',
    variantLabels: {
      marquee: 'ロゴ流し',
      static_grid: 'ロゴグリッド',
      press_quote: 'メディア引用',
      photo_stories: 'ストーリー写真',
    },
    variants: socialProofVariants,
    aiSchema: schemas.socialProofAISchema,
    uiSchema: schemas.socialProofUISchema,
    zodSchema: schemas.socialProofZodSchema,
    sample: schemas.socialProofSample,
  },
  stats: {
    type: 'stats',
    label: '統計・数値',
    variantLabels: {
      row_4: '4列横並び',
      cards_with_context: 'カード型',
      headline_pairing: '見出しペアリング',
    },
    variants: statsVariants,
    aiSchema: schemas.statsAISchema,
    uiSchema: schemas.statsUISchema,
    zodSchema: schemas.statsZodSchema,
    sample: schemas.statsSample,
  },
  product_showcase: {
    type: 'product_showcase',
    label: '製品紹介',
    variantLabels: {
      hero_cards: 'ヒーローカード',
      spec_compare: 'スペック比較表',
      switcher: 'タブ切替',
      editorial_lineup: 'エディトリアル並び',
    },
    variants: productShowcaseVariants,
    aiSchema: schemas.productShowcaseAISchema,
    uiSchema: schemas.productShowcaseUISchema,
    zodSchema: schemas.productShowcaseZodSchema,
    sample: schemas.productShowcaseSample,
  },
  video: {
    type: 'video',
    label: '動画',
    variantLabels: {
      hero_player: 'ヒーロー動画',
      gallery: 'ギャラリー',
      split_intro: '左右分割+チャプター',
      testimonial_reel: 'お客様の声リール',
    },
    variants: videoVariants,
    aiSchema: schemas.videoAISchema,
    uiSchema: schemas.videoUISchema,
    zodSchema: schemas.videoZodSchema,
    sample: schemas.videoSample,
  },
  image: {
    type: 'image',
    label: '画像',
    variantLabels: {
      gallery_carousel: '画像ギャラリー (カルーセル)',
      grid_3col: '3列グリッド',
      full_width: 'フル幅 1枚',
    },
    variants: imageVariants,
    aiSchema: schemas.imageAISchema,
    uiSchema: schemas.imageUISchema,
    zodSchema: schemas.imageZodSchema,
    sample: schemas.imageSample,
  },
  feature_grid: {
    type: 'feature_grid',
    label: '機能・特徴',
    variantLabels: {
      icon_3col: '3列アイコン',
      alt_rows: '交互レイアウト',
      tabs: 'タブ切替',
      image_cards: '画像カード',
    },
    variants: featureGridVariants,
    aiSchema: schemas.featureGridAISchema,
    uiSchema: schemas.featureGridUISchema,
    zodSchema: schemas.featureGridZodSchema,
    sample: schemas.featureGridSample,
  },
  bento: {
    type: 'bento',
    label: 'ベントグリッド',
    variantLabels: {
      classic_5: 'クラシック5枠',
      asymmetric_7: 'アシンメトリ7枠',
      stacked_mobile: 'スタック型',
      photo_bento: '写真ベント',
    },
    variants: bentoVariants,
    aiSchema: schemas.bentoAISchema,
    uiSchema: schemas.bentoUISchema,
    zodSchema: schemas.bentoZodSchema,
    sample: schemas.bentoSample,
  },
  how_it_works: {
    type: 'how_it_works',
    label: '仕組み・流れ',
    variantLabels: {
      horizontal_3: '横3ステップ',
      vertical_timeline: '縦タイムライン',
      screenshot_steps: 'スクリーンショット',
      image_cards: '画像カード',
    },
    variants: howItWorksVariants,
    aiSchema: schemas.howItWorksAISchema,
    uiSchema: schemas.howItWorksUISchema,
    zodSchema: schemas.howItWorksZodSchema,
    sample: schemas.howItWorksSample,
  },
  testimonials: {
    type: 'testimonials',
    label: 'お客様の声',
    variantLabels: {
      carousel: 'カルーセル',
      wall_3col: '3列ウォール',
      spotlight_video: 'スポットライト動画',
      photo_spotlight: '写真スポットライト',
    },
    variants: testimonialsVariants,
    aiSchema: schemas.testimonialsAISchema,
    uiSchema: schemas.testimonialsUISchema,
    zodSchema: schemas.testimonialsZodSchema,
    sample: schemas.testimonialsSample,
  },
  pricing: {
    type: 'pricing',
    label: '料金プラン',
    variantLabels: {
      three_tier: '3プラン',
      single_focus: '単一プラン',
      comparison: '比較表',
    },
    variants: pricingVariants,
    aiSchema: schemas.pricingAISchema,
    uiSchema: schemas.pricingUISchema,
    zodSchema: schemas.pricingZodSchema,
    sample: schemas.pricingSample,
  },
  comparison: {
    type: 'comparison',
    label: '比較',
    variantLabels: {
      us_vs_them: '当社 vs 他社',
      matrix: '比較マトリクス',
      before_after: 'ビフォーアフター',
    },
    variants: comparisonVariants,
    aiSchema: schemas.comparisonAISchema,
    uiSchema: schemas.comparisonUISchema,
    zodSchema: schemas.comparisonZodSchema,
    sample: schemas.comparisonSample,
  },
  faq: {
    type: 'faq',
    label: 'よくある質問',
    variantLabels: {
      accordion: 'アコーディオン',
      two_col: '2列',
      categorized: 'カテゴリ別',
    },
    variants: faqVariants,
    aiSchema: schemas.faqAISchema,
    uiSchema: schemas.faqUISchema,
    zodSchema: schemas.faqZodSchema,
    sample: schemas.faqSample,
  },
  cta_banner: {
    type: 'cta_banner',
    label: 'CTA バナー',
    variantLabels: {
      centered_giant: '中央大型',
      split_form: '左右分割+フォーム',
      card_floating: '浮遊カード',
      image_bg: '背景画像',
    },
    variants: ctaBannerVariants,
    aiSchema: schemas.ctaBannerAISchema,
    uiSchema: schemas.ctaBannerUISchema,
    zodSchema: schemas.ctaBannerZodSchema,
    sample: schemas.ctaBannerSample,
    hasCta: true,
  },
  newsletter: {
    type: 'newsletter',
    label: 'メルマガ登録',
    variantLabels: {
      inline_centered: 'インライン中央',
      card: 'カード型',
      magazine: 'マガジン風',
    },
    variants: newsletterVariants,
    aiSchema: schemas.newsletterAISchema,
    uiSchema: schemas.newsletterUISchema,
    zodSchema: schemas.newsletterZodSchema,
    sample: schemas.newsletterSample,
  },
  team: {
    type: 'team',
    // 用途: チーム紹介 / 開発者紹介 / 主催者紹介 等（複数人）
    label: 'チーム紹介',
    variantLabels: {
      grid_3col: '3列グリッド',
      founders_feature: '創業者フィーチャー',
      polaroid: 'ポラロイド風',
      photo_row: '写真の列',
    },
    variants: teamVariants,
    aiSchema: schemas.teamAISchema,
    uiSchema: schemas.teamUISchema,
    zodSchema: schemas.teamZodSchema,
    sample: schemas.teamSample,
  },
  speaker: {
    type: 'speaker',
    // 用途: 一人の登壇者 / 講師 / ゲスト紹介
    label: '登壇者紹介',
    variantLabels: {
      spotlight: 'スポットライト（左右分割）',
      card_centered: 'センターカード',
    },
    variants: speakerVariants,
    aiSchema: schemas.speakerAISchema,
    uiSchema: schemas.speakerUISchema,
    zodSchema: schemas.speakerZodSchema,
    sample: schemas.speakerSample,
  },
  location: {
    type: 'location',
    label: '位置情報',
    variantLabels: {
      map_left: '地図 左',
      map_right: '地図 右',
      map_full: '大型地図',
    },
    variants: locationVariants,
    aiSchema: schemas.locationAISchema,
    uiSchema: schemas.locationUISchema,
    zodSchema: schemas.locationZodSchema,
    sample: schemas.locationSample,
  },
  feature_spotlight: {
    type: 'feature_spotlight',
    label: '機能スポットライト',
    variantLabels: {
      image_left_richtext: '左画像 + 右テキスト',
      image_right_richtext: '左テキスト + 右画像',
      image_top_richtext: '上画像 + 下中央寄せ',
      floating_card: '背景画像 + 浮きカード',
    },
    variants: featureSpotlightVariants,
    aiSchema: schemas.featureSpotlightAISchema,
    uiSchema: schemas.featureSpotlightUISchema,
    zodSchema: schemas.featureSpotlightZodSchema,
    sample: schemas.featureSpotlightSample,
  },
  countdown: {
    type: 'countdown',
    label: 'カウントダウン',
    variantLabels: {
      thin_bar: '細帯バー (sticky 想定)',
      centered: '中央寄せ (目立つ)',
      split: '左右分割 (テキスト + クロック)',
    },
    variants: countdownVariants,
    aiSchema: schemas.countdownAISchema,
    uiSchema: schemas.countdownUISchema,
    zodSchema: schemas.countdownZodSchema,
    sample: schemas.countdownSample,
  },
  footer: {
    type: 'footer',
    label: 'フッター',
    variantLabels: {
      minimal: 'ミニマル',
      multi_col: '複数列',
      big_brand: 'ビッグブランド',
    },
    variants: footerVariants,
    aiSchema: schemas.footerAISchema,
    uiSchema: schemas.footerUISchema,
    zodSchema: schemas.footerZodSchema,
    sample: schemas.footerSample,
  },
};

/**
 * 与えられた section type と variant 名から、適切な React コンポーネントを取得する。
 * 未知の variant は各 type の最初の variant にフォールバック。
 */
export function getSectionComponent(
  type: SectionType,
  variant: string,
): ComponentType<SectionVariantProps> | null {
  const section = SECTIONS[type];
  if (!section) return null;
  const Comp = section.variants[variant];
  if (Comp) return Comp;
  const firstKey = Object.keys(section.variants)[0];
  return firstKey ? section.variants[firstKey] : null;
}

export function listVariants(type: SectionType): string[] {
  const section = SECTIONS[type];
  if (!section) return [];
  return Object.keys(section.variants);
}
